import { useMemo, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import { Segmented } from 'antd';
import {
  BankOutlined,
  CheckOutlined,
  CloseOutlined,
  ControlOutlined,
  CopyOutlined,
  DownloadOutlined,
  EditOutlined,
  FileDoneOutlined,
  FileUnknownOutlined,
  FormOutlined,
  InfoCircleOutlined,
  LoadingOutlined,
  PaperClipOutlined,
  UnorderedListOutlined,
  UploadOutlined,
} from '@ant-design/icons';
import { Sparkles } from 'lucide-react';
import { useSearchParams } from 'react-router-dom';

import { PageHeader } from '../../../components/shared';
import { Badge, Button, Field, Input, Select, Textarea } from '../../../components/ui';
import type { FieldControlProps } from '../../../components/ui';
import { useLoi } from '../../../hooks/useLoi';
import { useT } from '../../../i18n';
import type { KhoaDich } from '../../../i18n';
import { luuTep } from '../../../lib/luu-tep';
import { useToast } from '../../../lib/toast';
import { ThanhCongCuVanBan } from '../components/ThanhCongCuVanBan';
import { TrangVanBan } from '../components/soan-thao/TrangVanBan';
import type { TrangVanBanRef } from '../components/soan-thao/TrangVanBan';
import { useQuyenVanBan } from '../hooks/useQuyenVanBan';
import {
  useChiTietTomTat,
  useDanhSachTomTat,
  useDienTruongAi,
  useSoanVanBan,
  useTrichChuTuTep,
} from '../hooks/useTomTat';
import { useMauVanBan } from '../hooks/useVanBan';
import { tuTheThucMau } from '../nghiep-vu/mau-van-ban';
import type { TheThucNhap } from '../nghiep-vu/mau-van-ban';
import {
  TEP_TOI_DA,
  docTaiCho,
  docXuatSoan,
  duoiDuocNhan,
  lamSachTieuDe,
  noiDungNguon,
  tenTepSoan,
  theThucThieu,
  truongBatBuocThieu,
  xemTruocDienTruong,
} from '../nghiep-vu/soan-thao';
import type { KhoaTheThuc } from '../nghiep-vu/soan-thao';
import type { TruongMau } from '../types';
import type { YeuCauSoan } from '../types-tom-tat';

type CheDoNguon = 'list' | 'upload';
interface NguonSoan {
  title: string;
  original: string;
  summary: string;
}

const NHAN_THE_THUC: Record<KhoaTheThuc, KhoaDich> = {
  cqch: 'vanBanSoanThao.tt.cqch',
  cq: 'vanBanSoanThao.tt.cq',
  so: 'vanBanSoanThao.tt.so',
  kyhieu: 'vanBanSoanThao.tt.kyhieu',
  dd: 'vanBanSoanThao.tt.dd',
  ky: 'vanBanSoanThao.tt.ky',
  cv: 'vanBanSoanThao.tt.cv',
};

function Khoi({ tieuDe, icon, phai, children }: { tieuDe: string; icon: ReactNode; phai?: ReactNode; children: ReactNode }) {
  return (
    <section className="rounded-lg border border-default bg-surface p-4 shadow-sm">
      <div className="mb-3.5 flex flex-wrap items-center justify-between gap-x-2 gap-y-1 border-b border-default pb-2.5">
        <h2 className="flex items-center gap-2 whitespace-nowrap text-sm font-semibold text-content-primary">
          {icon} {tieuDe}
        </h2>
        {phai}
      </div>
      {children}
    </section>
  );
}

function OTruong({ field, value, onChange, p }: { field: TruongMau; value: string; onChange: (v: string) => void; p: FieldControlProps }) {
  const { t } = useT();
  if (field.type === 'TEXTAREA') {
    return <Textarea {...p} rows={3} className="resize-none" value={value} onChange={(e) => onChange(e.target.value)} />;
  }
  if (field.type === 'SELECT') {
    return (
      <Select
        {...p}
        className="w-full"
        placeholder={t('vanBanSoanThao.chon')}
        value={value || undefined}
        options={(field.options ?? []).map((o) => ({ value: o, label: o }))}
        onChange={(v) => onChange(String(v ?? ''))}
      />
    );
  }
  return (
    <Input
      {...p}
      type={field.type === 'DATE' ? 'date' : field.type === 'NUMBER' ? 'number' : 'text'}
      value={value}
      onChange={(e) => onChange(e.target.value)}
    />
  );
}

/** Soạn thảo văn bản (`/xu-ly-van-ban/soan-thao`), chuyển từ foxainative. Nhận `?src=` (từ Tóm tắt) và `?tpl=` (từ Mẫu văn bản). */
export function SoanThaoView() {
  const { t } = useT();
  const dichLoi = useLoi();
  const toast = useToast();
  const [thamSo] = useSearchParams();
  const coTheSoan = useQuyenVanBan('compose').canCreate;

  const tomTat = useDanhSachTomTat();
  const mau = useMauVanBan();
  const soan = useSoanVanBan();
  const dienAi = useDienTruongAi();
  const trichChu = useTrichChuTuTep();

  const [cheDo, setCheDo] = useState<CheDoNguon>('list');
  const [srcId, setSrcId] = useState<string | undefined>(thamSo.get('src') ?? undefined);
  const [tplId, setTplId] = useState<string | undefined>(thamSo.get('tpl') ?? undefined);
  const [tepTai, setTepTai] = useState<NguonSoan | null>(null);
  const [dangDoc, setDangDoc] = useState(false);
  const [loiTep, setLoiTep] = useState<string | null>(null);
  /** `null` = chưa sửa, dùng thể thức mặc định của mẫu. */
  const [sua, setSua] = useState<TheThucNhap | null>(null);
  const [values, setValues] = useState<Record<string, string>>({});
  const [banSoan, setBanSoan] = useState<string | null>(null);
  const [lan, setLan] = useState(0);
  const [dangSua, setDangSua] = useState(false);
  const lanTai = useRef(0);
  const trang = useRef<TrangVanBanRef>(null);

  const chiTietNguon = useChiTietTomTat(cheDo === 'list' ? (srcId ?? null) : null);
  const vanBanGoc = useMemo(
    () => (tomTat.data?.data ?? []).filter((d) => d.status === 'COMPLETED').map((d) => ({ value: d.id, label: d.document_name })),
    [tomTat.data],
  );
  const mauDangDung = useMemo(() => (mau.data?.data ?? []).filter((m) => m.status === 'DANG_DUNG'), [mau.data]);
  const tpl = mauDangDung.find((m) => m.id === tplId) ?? null;
  // Mẫu từ `?tpl=` đã ngưng dùng hay không tồn tại: báo cạnh ô chọn mẫu
  const mauKhongCo = !!tplId && !!mau.data && !tpl;

  const nguon: NguonSoan | null =
    cheDo === 'upload'
      ? tepTai
      : chiTietNguon.data
        ? {
            title: chiTietNguon.data.document_name,
            original: chiTietNguon.data.original_content ?? '',
            summary: chiTietNguon.data.summary_content ?? '',
          }
        : null;
  const laDienTruong = tpl?.compose_mode === 'FIELD_FILL';
  const fields = tpl?.fields ?? [];
  const coTheAiDien = fields.some((f) => f.ai_fill !== false);
  const info = useMemo(() => sua ?? tuTheThucMau(tpl?.default_meta), [sua, tpl]);

  const xemTruocTrucTiep = useMemo(() => {
    if (!laDienTruong || !tpl?.body_template) return null;
    return xemTruocDienTruong(tpl.body_template, values, tpl.fields, info, tpl.name, tpl.symbol_code);
  }, [laDienTruong, tpl, values, info]);
  const xemTruoc = banSoan ?? xemTruocTrucTiep;

  const boBanSoan = () => {
    setBanSoan(null);
    setDangSua(false);
  };
  /** Đổi ô nhập → bản đã đóng băng về chế độ xem trực tiếp; chỉ mẫu điền trường có bản trực tiếp thay thế. */
  const moBang = () => {
    if (laDienTruong) boBanSoan();
  };

  const chonMau = (id: string | undefined) => {
    setTplId(id);
    setSua(null);
    setValues({});
    boBanSoan();
  };
  const chonNguon = (id: string | undefined) => {
    setSrcId(id);
    boBanSoan();
  };
  const doiTheThuc = (k: keyof TheThucNhap, v: string) => {
    setSua({ ...info, [k]: v });
    moBang();
  };
  const doiGiaTri = (k: string, v: string) => {
    setValues((cu) => ({ ...cu, [k]: v }));
    moBang();
  };

  const goTep = () => {
    lanTai.current += 1;
    setDangDoc(false);
    setTepTai(null);
    setLoiTep(null);
    boBanSoan();
  };

  const docTep = async (tep: File) => {
    const lanNay = ++lanTai.current;
    const xongLoi = (cau: string) => {
      if (lanNay !== lanTai.current) return;
      setDangDoc(false);
      setTepTai(null);
      setLoiTep(cau);
    };
    if (!duoiDuocNhan(tep.name)) return xongLoi(t('vanBanSoanThao.loiDinhDang'));
    if (tep.size > TEP_TOI_DA) return xongLoi(t('vanBanSoanThao.loiQuaLon'));
    setLoiTep(null);
    setTepTai(null);
    setDangDoc(true);
    try {
      const chu = docTaiCho(tep.name) ? await tep.text() : (await trichChu.mutateAsync(tep)).text;
      // Chọn liên tiếp hai tệp: chỉ nhận kết quả của tệp chọn sau cùng
      if (lanNay !== lanTai.current) return;
      if (!chu.trim()) return xongLoi(t('vanBanSoanThao.loiKhongDocDuoc'));
      setDangDoc(false);
      setTepTai({ title: lamSachTieuDe(tep.name), original: chu, summary: '' });
      boBanSoan();
    } catch (e) {
      xongLoi(dichLoi(e));
    }
  };

  const aiDien = () => {
    if (!tpl) return;
    if (dangDoc) return toast.error(t('vanBanSoanThao.dangDocTep'));
    const nd = noiDungNguon(nguon);
    if (nd.length < 10) return toast.error(t('vanBanSoanThao.canNguonDeAiDien'));
    dienAi.mutate(
      { template_id: tpl.id, source_content: nd, keys: fields.filter((f) => f.ai_fill !== false).map((f) => f.key) },
      {
        onSuccess: (kq) => {
          setValues((cu) => ({ ...cu, ...kq.values }));
          moBang();
          toast.success(t('vanBanSoanThao.daAiDien'));
        },
      },
    );
  };

  const xuLy = () => {
    if (!tpl) return toast.error(t('vanBanSoanThao.chuaChonMau'));
    if (dangDoc) return toast.error(t('vanBanSoanThao.dangDocTep'));
    const nd = noiDungNguon(nguon);
    if (!laDienTruong) {
      if (!nguon || nd.length < 10) return toast.error(t('vanBanSoanThao.canNguon'));
      const thieu = theThucThieu(info);
      if (thieu.length) {
        return toast.error(t('vanBanSoanThao.thieuTheThuc', { ds: thieu.map((k) => t(NHAN_THE_THUC[k])).join(', ') }));
      }
    } else {
      const thieu = truongBatBuocThieu(fields, values);
      if (thieu.length) return toast.error(t('vanBanSoanThao.thieuTruong', { ds: thieu.join(', ') }));
    }
    const than: YeuCauSoan = {
      template_id: tpl.id,
      cqch: info.cqch.trim(),
      cq: info.cq.trim(),
      so: info.so.trim(),
      kyhieu: info.kyhieu.trim(),
      dd: info.dd.trim(),
      ky: info.ky.trim(),
      cv: info.cv.trim(),
      noinhan: info.nn.trim(),
    };
    if (nd) than.source_content = nd;
    if (nguon?.title) than.source_title = nguon.title;
    if (fields.length) than.values = values;
    soan.mutate(than, {
      onSuccess: (kq) => {
        setBanSoan(kq.html);
        setLan((n) => n + 1);
        setDangSua(false);
        if (kq.missing_keys?.length) toast.error(t('vanBanSoanThao.conTruongChuaDien', { ds: kq.missing_keys.join(', ') }));
      },
    });
  };

  const batDauSua = () => {
    if (banSoan == null && xemTruocTrucTiep != null) setBanSoan(xemTruocTrucTiep);
    setDangSua(true);
  };
  const xongSua = () => {
    setBanSoan(trang.current?.html() ?? banSoan);
    setDangSua(false);
  };
  const saoChep = () => {
    const chu = trang.current?.chu() ?? '';
    if (navigator.clipboard) void navigator.clipboard.writeText(chu).then(() => toast.success(t('vanBanSoanThao.daSaoChep')));
  };
  const xuat = () => {
    const html = trang.current?.html() ?? '';
    luuTep(new Blob(['﻿' + docXuatSoan(html)], { type: 'application/msword' }), tenTepSoan(tpl?.name));
  };

  const trichDoan = nguon ? `${nguon.original.replace(/\s+/g, ' ').slice(0, 200)}…` : null;
  const batBuocTheThuc = !laDienTruong;
  const oTheThuc = (k: keyof TheThucNhap, khoa: KhoaDich, batBuoc = batBuocTheThuc) => (
    <Field label={t(khoa)} required={batBuoc}>
      {(p) => <Input {...p} value={info[k] as string} onChange={(e) => doiTheThuc(k, e.target.value)} />}
    </Field>
  );

  return (
    <div className="flex h-full flex-col gap-4 overflow-hidden bg-subtle p-4">
      <PageHeader
        breadcrumb={[{ label: t('nav.muc.xuLyVanBan') }, { label: t('vanBanSoanThao.tieuDe') }]}
        titleIcon={<FormOutlined className="text-primary-600" />}
        title={t('vanBanSoanThao.tieuDe')}
        subtitle={t('vanBanSoanThao.moTa')}
      />

      <div className="grid min-h-0 flex-1 grid-cols-1 items-start gap-3.5 overflow-y-auto lg:grid-cols-[370px_1fr] lg:items-stretch lg:overflow-visible">
        {/* Trái: thiết lập — cuộn riêng ở màn rộng */}
        <div className="space-y-3.5 lg:min-h-0 lg:overflow-y-auto lg:pr-1">
          <Khoi tieuDe={t('vanBanSoanThao.thietLap')} icon={<ControlOutlined className="text-primary-500" />}>
            <div className="space-y-4">
              <div>
                <Field
                  label={laDienTruong ? t('vanBanSoanThao.oNguonTuyChon') : t('vanBanSoanThao.oNguon')}
                  required={!laDienTruong}
                  error={cheDo === 'upload' ? (loiTep ?? undefined) : undefined}
                >
                  {(p) => (
                    <div className="space-y-2.5">
                      <Segmented<CheDoNguon>
                        value={cheDo}
                        onChange={(v) => {
                          setCheDo(v);
                          boBanSoan();
                        }}
                        options={[
                          { value: 'list', label: t('vanBanSoanThao.tuTomTat'), icon: <UnorderedListOutlined /> },
                          { value: 'upload', label: t('vanBanSoanThao.taiMoi'), icon: <UploadOutlined /> },
                        ]}
                      />
                      {cheDo === 'list' ? (
                        <Select
                          {...p}
                          className="w-full"
                          showSearch
                          optionFilterProp="label"
                          allowClear
                          loading={tomTat.isLoading}
                          placeholder={t('vanBanSoanThao.chonVanBanGoc')}
                          value={srcId}
                          options={vanBanGoc}
                          notFoundContent={tomTat.error ? dichLoi(tomTat.error) : t('vanBanSoanThao.chuaCoVanBanGoc')}
                          onChange={(v) => chonNguon(v as string | undefined)}
                        />
                      ) : (
                        <div>
                          <input
                            {...p}
                            type="file"
                            accept=".txt,.md,.pdf,.docx"
                            disabled={dangDoc}
                            onChange={(e) => {
                              const tep = e.target.files?.[0];
                              if (tep) void docTep(tep);
                              e.target.value = '';
                            }}
                            className="w-full rounded border border-default bg-surface px-3 py-1.5 text-sm text-content-secondary file:mr-3 file:rounded-sm file:border-0 file:bg-subtle file:px-2 file:py-1 file:text-xs disabled:cursor-not-allowed disabled:opacity-50"
                          />
                          {dangDoc ? (
                            <div className="mt-2 inline-flex items-center gap-2 rounded border border-default bg-subtle px-3 py-1.5 text-xs text-content-secondary">
                              <LoadingOutlined /> {t('vanBanSoanThao.dangDocNoiDung')}
                            </div>
                          ) : tepTai ? (
                            <div className="mt-2 inline-flex max-w-full items-center gap-2 rounded border border-primary-200 bg-primary-50 px-3 py-1.5 text-xs text-primary-700">
                              <PaperClipOutlined className="shrink-0" />
                              <span className="truncate">{tepTai.title}</span>
                              <button
                                type="button"
                                onClick={goTep}
                                aria-label={t('vanBanSoanThao.goTep')}
                                title={t('vanBanSoanThao.goTep')}
                                className="shrink-0 rounded-sm p-0.5 text-primary-600 transition-colors hover:bg-primary-100 hover:text-primary-700"
                              >
                                <CloseOutlined />
                              </button>
                            </div>
                          ) : (
                            !loiTep && <div className="mt-2 text-xs text-content-muted">{t('vanBanSoanThao.chuaTaiTep')}</div>
                          )}
                        </div>
                      )}
                    </div>
                  )}
                </Field>
                {trichDoan && (
                  <div className="mt-2 rounded border border-dashed border-default bg-subtle px-3 py-2 text-xs leading-relaxed text-content-secondary">
                    <InfoCircleOutlined className="text-primary-500" /> <b>{t('vanBanSoanThao.trichDoan')}</b> {trichDoan}
                  </div>
                )}
              </div>

              <div>
                <Field label={t('vanBanSoanThao.oMau')} required error={mauKhongCo ? t('vanBanSoanThao.mauKhongCo') : undefined}>
                  {(p) => (
                    <Select
                      {...p}
                      className="w-full"
                      showSearch
                      optionFilterProp="label"
                      loading={mau.isLoading}
                      placeholder={t('vanBanSoanThao.chonMau')}
                      value={tpl ? tplId : undefined}
                      options={mauDangDung.map((m) => ({ value: m.id, label: m.name }))}
                      notFoundContent={mau.error ? dichLoi(mau.error) : undefined}
                      onChange={(v) => chonMau(v as string | undefined)}
                    />
                  )}
                </Field>
                {tpl && (
                  <div className="mt-2 rounded border border-dashed border-default bg-subtle px-3 py-2 text-xs leading-relaxed text-content-secondary">
                    <div className="mb-1">
                      <Badge variant={laDienTruong ? 'violet' : 'primary'}>
                        {laDienTruong ? t('vanBanSoanThao.cheDoDien') : t('vanBanSoanThao.cheDoAi')}
                      </Badge>
                    </div>
                    <InfoCircleOutlined className="text-primary-500" /> <b>{tpl.name}:</b> {tpl.description}
                  </div>
                )}
              </div>
            </div>
          </Khoi>

          {/* Thể thức: điền sẵn từ default_meta của mẫu, sửa được theo từng văn bản; bắt buộc với mẫu AI soạn */}
          {tpl && (
            <Khoi
              tieuDe={t('vanBanSoanThao.theThuc')}
              icon={<BankOutlined className="text-primary-500" />}
              phai={
                <span className="whitespace-nowrap text-2xs font-normal text-content-muted">
                  {laDienTruong ? t('vanBanSoanThao.theThucTuyChon') : t('vanBanSoanThao.theThucTuMau')}
                </span>
              }
            >
              <div className="space-y-3.5">
                {oTheThuc('cqch', 'vanBanSoanThao.tt.cqch')}
                {oTheThuc('cq', 'vanBanSoanThao.tt.cq')}
                <div className="grid grid-cols-2 gap-3.5">
                  {oTheThuc('so', 'vanBanSoanThao.tt.so')}
                  {oTheThuc('kyhieu', 'vanBanSoanThao.tt.kyhieu')}
                </div>
                {oTheThuc('dd', 'vanBanSoanThao.tt.dd')}
                <div className="grid grid-cols-2 gap-3.5">
                  {oTheThuc('ky', 'vanBanSoanThao.tt.ky')}
                  {oTheThuc('cv', 'vanBanSoanThao.tt.cv')}
                </div>
                <Field label={t('vanBanSoanThao.tt.noinhan')}>
                  {(p) => (
                    <Textarea
                      {...p}
                      rows={3}
                      className="resize-none"
                      value={info.nn}
                      placeholder={t('vanBanSoanThao.noiNhanViDu')}
                      onChange={(e) => doiTheThuc('nn', e.target.value)}
                    />
                  )}
                </Field>
              </div>
            </Khoi>
          )}

          {/* Nội dung cần điền theo mẫu: mẫu tuỳ chỉnh và mẫu lai */}
          {tpl && fields.length > 0 && (
            <Khoi
              tieuDe={t('vanBanSoanThao.noiDungDien')}
              icon={<FormOutlined className="text-primary-500" />}
              phai={
                coTheAiDien && coTheSoan ? (
                  <Button
                    variant="outline"
                    size="sm"
                    className="ai-glow"
                    icon={<Sparkles size={14} className="ai-sparkle" />}
                    loading={dienAi.isPending}
                    onClick={aiDien}
                  >
                    {t('vanBanSoanThao.aiDien')}
                  </Button>
                ) : null
              }
            >
              <div className="space-y-3.5">
                {fields.map((f) => (
                  <Field
                    key={f.key}
                    label={t(f.ai_fill !== false ? 'vanBanSoanThao.nhanAiDienDuoc' : 'vanBanSoanThao.nhanTuNhap', { nhan: f.label })}
                    required={f.required}
                    hint={f.hint ?? undefined}
                  >
                    {(p) => <OTruong field={f} p={p} value={values[f.key] ?? ''} onChange={(v) => doiGiaTri(f.key, v)} />}
                  </Field>
                ))}
              </div>
            </Khoi>
          )}

          {coTheSoan && (
            <Button
              variant="primary"
              block
              size="lg"
              className={laDienTruong ? '' : 'ai-glow'}
              onClick={xuLy}
              loading={soan.isPending}
              disabled={dangDoc}
              icon={laDienTruong ? <FileDoneOutlined /> : <Sparkles size={16} className="ai-sparkle" />}
            >
              {soan.isPending
                ? t('vanBanSoanThao.dangXuLy')
                : dangDoc
                  ? t('vanBanSoanThao.dangDocTepNgan')
                  : laDienTruong
                    ? t('vanBanSoanThao.taoHoanChinh')
                    : t('vanBanSoanThao.aiSoan')}
            </Button>
          )}
        </div>

        {/* Phải: văn bản sau khi soạn */}
        <div className="flex min-h-0 flex-col overflow-hidden rounded-lg border border-default bg-surface shadow-sm">
          <div className="flex flex-wrap items-center justify-between gap-2 border-b border-default bg-subtle px-4 py-3">
            <h2 className="flex items-center gap-1.5 text-sm font-semibold text-content-primary">
              <FileDoneOutlined className="text-primary-500" /> {t('vanBanSoanThao.vanBanSauSoan')}
            </h2>
            {xemTruoc != null && (
              <span className="flex flex-wrap items-center gap-1.5">
                {dangSua ? (
                  <Button variant="primary" size="sm" icon={<CheckOutlined />} onClick={xongSua}>
                    {t('vanBanSoanThao.xongSua')}
                  </Button>
                ) : (
                  <Button variant="outline" size="sm" icon={<EditOutlined />} onClick={batDauSua}>
                    {t('vanBanSoanThao.suaVanBan')}
                  </Button>
                )}
                <Button variant="outline" size="sm" icon={<CopyOutlined />} onClick={saoChep}>
                  {t('vanBanSoanThao.saoChep')}
                </Button>
                <Button variant="primary" size="sm" icon={<DownloadOutlined />} onClick={xuat}>
                  {t('vanBanSoanThao.xuatTep')}
                </Button>
              </span>
            )}
          </div>

          {dangSua && <ThanhCongCuVanBan className="shrink-0" />}

          {xemTruoc == null ? (
            <div className="flex flex-1 flex-col items-center justify-center p-10 text-center text-content-muted">
              <FileUnknownOutlined className="mb-3 text-4xl" />
              <p className="max-w-sm text-sm leading-relaxed">{t('vanBanSoanThao.rongHuongDan')}</p>
            </div>
          ) : (
            <TrangVanBan ref={trang} html={xemTruoc} lan={lan} dangSua={dangSua} />
          )}
        </div>
      </div>
    </div>
  );
}
